/**
 * Cross-surface "show me this selection" channel for feedback anchors.
 *
 * The AI QA panel (aside) asks; the report's Content view answers. A prop
 * chain would thread through four layers and two ownership boundaries, so
 * this uses a module-level pending slot plus a DOM event:
 *
 * - `requestAnchorHighlight(paraIds)` remembers the ask and broadcasts it.
 * - `ReportBody` listens and flips itself to the Content tab.
 * - `DocxContent` consumes the pending ask once its blocks are in the DOM
 *   (freshly mounted by the tab flip, or already live), scrolls the first
 *   block into view and flashes every anchored block.
 *
 * The slot makes the ask survive the tab flip: the event fires before the
 * Content view exists (`keepMounted=false`), so the view pulls the pending
 * ask on mount rather than relying on having heard the event.
 */

export const ANCHOR_HIGHLIGHT_EVENT = 'gti:highlight-anchors';
export const ANCHOR_PERSIST_EVENT = 'gti:persist-anchors';

let pendingParaIds: string[] | null = null;
let persistentParaIds: string[] = [];

export function requestAnchorHighlight(paraIds: string[]): void {
  if (paraIds.length === 0) {
    return;
  }
  pendingParaIds = paraIds;
  window.dispatchEvent(new CustomEvent(ANCHOR_HIGHLIGHT_EVENT));
}

/** One-shot read: the first consumer wins, later calls see null. */
export function consumePendingAnchorHighlight(): string[] | null {
  const ids = pendingParaIds;
  pendingParaIds = null;
  return ids;
}

/**
 * Persistent highlight — the compose-time "what is selected" marker.
 * Set with the captured selection, cleared ([]) on send/clear/unmount;
 * the Content view keeps the marked blocks tinted the whole time.
 */
export function setPersistentAnchorHighlight(paraIds: string[]): void {
  persistentParaIds = paraIds;
  window.dispatchEvent(new CustomEvent(ANCHOR_PERSIST_EVENT));
}

export function getPersistentAnchorHighlight(): string[] {
  return persistentParaIds;
}

/** Test hook: reset module state between tests. */
export function clearPendingAnchorHighlight(): void {
  pendingParaIds = null;
  persistentParaIds = [];
}
